import { useEffect, useRef, useState } from "react";

function DropdownMenu({ open, onClose, children, className = "min-w-[80px]" }) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 z-[100]" onClick={onClose} aria-hidden="true" />
      <div
        className={`absolute left-0 z-[101] mt-1 rounded-md border border-gray-200 bg-white py-1 shadow-lg ${className}`}
      >
        {children}
      </div>
    </>
  );
}

export default function AtlasResultsMeta({
  startRow = 0,
  endRow = 0,
  totalCount = 0,
  pageSize = 15,
  pageSizeOptions = [15, 25, 50, 100, 200],
  onPageSizeChange,
  showSort = true,
  sortBy,
  sortOrder = "asc",
  sortOptions = [],
  sortByLabel,
  orderLabel,
  onSortByChange,
  onSortOrderToggle,
  className = "",
}) {
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const pageRef = useRef(null);
  const sortRef = useRef(null);

  const resolvedSortLabel =
    sortByLabel ||
    sortOptions.find((o) => o.value === sortBy)?.label ||
    sortBy ||
    "—";
  const resolvedOrderLabel =
    orderLabel || (sortOrder === "asc" ? "Ascending" : "Descending");

  useEffect(() => {
    if (!pageMenuOpen && !sortMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setPageMenuOpen(false);
        setSortMenuOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pageMenuOpen, sortMenuOpen]);

  return (
    <div
      className={`flex min-w-0 max-w-full flex-wrap items-center gap-1.5 px-1 text-sm text-muted-foreground ${className}`}
    >
      <span>Showing</span>
      <span className="relative inline-block" ref={pageRef}>
        <button
          type="button"
          className="cursor-pointer font-medium text-red-600 hover:underline"
          onClick={() => setPageMenuOpen((v) => !v)}
          title="Change rows per page"
        >
          {startRow}-{endRow}
        </button>
        <DropdownMenu open={pageMenuOpen} onClose={() => setPageMenuOpen(false)}>
          {pageSizeOptions.map((size) => (
            <button
              key={size}
              type="button"
              className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100 ${
                pageSize === size ? "bg-red-50 text-red-600" : "text-gray-700"
              }`}
              onClick={() => {
                onPageSizeChange?.(size);
                setPageMenuOpen(false);
              }}
            >
              {size}
            </button>
          ))}
        </DropdownMenu>
      </span>
      <span>of {Number(totalCount).toLocaleString()} results</span>
      {showSort && sortOptions.length > 0 ? (
        <>
          <span>sorted by</span>
          <span className="relative inline-block" ref={sortRef}>
            <button
              type="button"
              className="font-medium text-red-600 hover:underline"
              onClick={() => setSortMenuOpen((v) => !v)}
            >
              {resolvedSortLabel}
            </button>
            <DropdownMenu
              open={sortMenuOpen}
              onClose={() => setSortMenuOpen(false)}
              className="min-w-[140px]"
            >
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`block w-full px-3 py-1.5 text-left text-xs hover:bg-gray-100 ${
                    sortBy === opt.value ? "bg-red-50 text-red-600" : "text-gray-700"
                  }`}
                  onClick={() => {
                    onSortByChange?.(opt.value);
                    setSortMenuOpen(false);
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </DropdownMenu>
          </span>
          <span>,</span>
          <button
            type="button"
            className="font-medium text-red-600 hover:underline"
            onClick={onSortOrderToggle}
          >
            {resolvedOrderLabel}
          </button>
        </>
      ) : null}
    </div>
  );
}
